// Paywall 2 (contracts/paywall.md; backend/api src/domain/paywall.ts): the open-time rules, the variant shape
// experiments use, offers, list prices, and the paywall.* config keys as the API validates them (including the
// cross-key rule: every product a paywall value uses is in paywall.allowedProducts). Shared by the pages and the
// mock (src/mocks/db.ts validates PUT and PATCH /config with these schemas).

import { z } from "zod";
import { LOCALES } from "../api/messaging";

export const LAYOUTS = ["classic", "timeline", "compare"] as const;
export type PaywallLayout = (typeof LAYOUTS)[number];
export const PLANS = ["monthly", "annual", "lifetime"] as const;
export type Plan = (typeof PLANS)[number];
export const RETURNING = ["off", "every_open", "daily", "every_n_days"] as const;
export type Returning = (typeof RETURNING)[number];
export const EXPERIMENT_STATUSES = ["draft", "running", "paused", "ended"] as const;
export type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];
export const VARIANT_IDS = ["a", "b", "c", "d"] as const;
export type VariantId = (typeof VARIANT_IDS)[number];
export const PROMO_AUDIENCES = ["lapsed", "renewal_off"] as const;

export const BASE_PRODUCTS: Record<Plan, string> = { monthly: "iqdaily.pro.monthly", annual: "iqdaily.pro.annual", lifetime: "iqdaily.pro.lifetime" };
export const ANNUAL_OFFER_PRODUCT = "iqdaily.pro.annual.offer";
export const LAYOUT_LABEL: Record<PaywallLayout, string> = { classic: "Classic", timeline: "Trial timeline", compare: "Free vs Pro" };
export const LAYOUT_HINT: Record<PaywallLayout, string> = {
  classic: "Today's paywall (K1): plans stacked, the selected one highlighted.",
  timeline: "The trial as a timeline: today, a reminder on day 5, billing on day 7.",
  compare: "A Free vs Pro table above the plans.",
};
export const PLAN_NAME: Record<Plan, string> = { monthly: "Monthly", annual: "Yearly", lifetime: "Lifetime" };
export const RETURNING_LABEL: Record<Returning, string> = { off: "Never", every_open: "Every fresh open", daily: "Once a day", every_n_days: "Every N days" };
export const STATUS_TONE: Record<ExperimentStatus, string> = { draft: "neutral", running: "success", paused: "warning", ended: "muted" };

/** Copy limits (contracts/paywall.md §3, §4). */
export const COPY_LIMITS = { headline: 60, subheadline: 120, title: 60, body: 160, badge: 20 } as const;
export const CLOSE_DELAY_MAX = 3;
export const WEIGHT = { min: 1, max: 1000 } as const;
export const EXPERIMENT_NAME_MAX = 120;

// ---------------------------------------------------------------- shapes

const distinct = <T>(xs: readonly T[]) => new Set(xs).size === xs.length;

/** `iqdaily.pro.<plan>` or an alternative of that plan, `iqdaily.pro.<plan>.<suffix>` (price tests, offers). */
const productRegex = (plan: string) => new RegExp(`^iqdaily\\.pro\\.${plan}(\\.[a-z0-9_]{1,32}){0,3}$`);
export const PRODUCT_RE = productRegex("(monthly|annual|lifetime)");
export const ProductId = z.string().max(100).regex(PRODUCT_RE, "expected iqdaily.pro.<monthly|annual|lifetime>[.<suffix>]");
const productOf = (plan: Plan) => z.string().max(100).regex(productRegex(plan), `expected an iqdaily.pro.${plan}[.<suffix>] product id`);

/** The plan of a product id by its name (iqdaily.pro.annual.offer → annual); null when it isn't one. */
export function planOfProduct(productId: string): Plan | null {
  for (const plan of PLANS) if (productRegex(plan).test(productId)) return plan;
  return null;
}

/** A localized string: `en` required, any other app locale optional, each 1–max characters. */
const localizedText = (max: number) =>
  z.partialRecord(z.enum(LOCALES), z.string().trim().min(1).max(max)).refine((m) => m.en !== undefined, { message: 'needs an "en" text' });
export type LocalizedText = Partial<Record<(typeof LOCALES)[number], string>>;

/** A paywall variant: every field optional (missing ones use paywall.defaultVariant, then the app's paywall). */
export const Variant = z
  .strictObject({
    layout: z.enum(LAYOUTS).optional(),
    headline: localizedText(COPY_LIMITS.headline).optional(),
    subheadline: localizedText(COPY_LIMITS.subheadline).optional(),
    plans: z.array(z.enum(PLANS)).min(1).max(3).optional(),
    defaultPlan: z.enum(PLANS).optional(),
    products: z.strictObject({ monthly: productOf("monthly").optional(), annual: productOf("annual").optional(), lifetime: productOf("lifetime").optional() }).optional(),
    showSavings: z.boolean().optional(),
    closeDelaySec: z.number().min(0).max(CLOSE_DELAY_MAX).optional(),
    exitOffer: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.plans) {
      if (!distinct(v.plans)) ctx.addIssue({ code: "custom", path: ["plans"], message: "plans must be distinct" });
      if (!v.plans.some((p) => p !== "lifetime")) ctx.addIssue({ code: "custom", path: ["plans"], message: "plans need at least one subscription (monthly or annual)" });
      if (v.defaultPlan && !v.plans.includes(v.defaultPlan)) ctx.addIssue({ code: "custom", path: ["defaultPlan"], message: "defaultPlan must be one of plans" });
    }
  });
export type Variant = z.infer<typeof Variant>;

/** Product ids a variant overrides. */
export const variantProducts = (v: Variant): string[] => Object.values(v.products ?? {}).filter((p): p is string => typeof p === "string");

/** What the API would refuse in a variant, in words; plus its products outside paywall.allowedProducts. */
export function variantProblems(v: Variant, allowed: readonly string[]): string[] {
  const out: string[] = [];
  if (v.plans) {
    if (!v.plans.some((p) => p !== "lifetime")) out.push("Show at least one subscription (monthly or yearly).");
    if (!distinct(v.plans)) out.push("Each plan once.");
    if (v.defaultPlan && !v.plans.includes(v.defaultPlan)) out.push("The preselected plan must be one of the plans shown.");
  }
  for (const [plan, id] of Object.entries(v.products ?? {})) {
    if (!id) continue;
    if (!productRegex(plan).test(id)) out.push(`${PLAN_NAME[plan as Plan]}: ${id} isn't an iqdaily.pro.${plan} product.`);
    else if (!allowed.includes(id)) out.push(`${PLAN_NAME[plan as Plan]}: ${id} isn't in paywall.allowedProducts.`);
  }
  if (v.closeDelaySec !== undefined && (!(v.closeDelaySec >= 0) || v.closeDelaySec > CLOSE_DELAY_MAX)) out.push(`The close button appears within ${CLOSE_DELAY_MAX} s (App Review).`);
  for (const [field, max] of [
    ["headline", COPY_LIMITS.headline],
    ["subheadline", COPY_LIMITS.subheadline],
  ] as const) {
    const map = v[field];
    if (!map) continue;
    if (!map.en?.trim()) out.push(`The ${field} needs English when any language has one.`);
    for (const [l, t] of Object.entries(map)) if ((t?.length ?? 0) > max) out.push(`${l}: the ${field} is at most ${max} characters.`);
  }
  return out;
}

/** The app's built-in paywall: what an unset field means in the preview (the config's default variant is empty). */
export const APP_PAYWALL: Variant = {
  layout: "classic",
  headline: { en: "Unlock everything" },
  subheadline: { en: "Unlimited levels, Hard Mode, the full archive and no ads." },
  plans: ["annual", "monthly", "lifetime"],
  defaultPlan: "annual",
  showSavings: true,
  closeDelaySec: 0,
  exitOffer: false,
};

/** A variant over another: the variant's fields win (as the API's mergeVariant). */
export function mergeVariant(base: Variant, over: Variant): Variant {
  const out: Variant = { ...base };
  for (const [k, val] of Object.entries(over)) if (val !== undefined) (out as Record<string, unknown>)[k] = val;
  if (base.products || over.products) out.products = { ...base.products, ...over.products };
  if (out.plans && out.defaultPlan && !out.plans.includes(out.defaultPlan)) delete out.defaultPlan;
  return out;
}

export const OnOpen = z.strictObject({
  firstLaunch: z.boolean(),
  returning: z.enum(RETURNING),
  everyDays: z.int().min(1).max(60),
  minHoursBetween: z.number().min(0).max(24 * 14),
});
export type OnOpen = z.infer<typeof OnOpen>;

export const OfferCopy = z
  .partialRecord(z.enum(LOCALES), z.strictObject({ title: z.string().trim().max(60).optional(), body: z.string().trim().max(160).optional(), badge: z.string().trim().max(20).optional() }))
  .refine((c) => Boolean(c.en?.title && c.en.body), { message: 'content needs "en" with a title and a body' });
export const ExitOffer = z.strictObject({ enabled: z.boolean(), productId: ProductId, content: OfferCopy, maxShows: z.int().min(1).max(5) });
export type ExitOffer = z.infer<typeof ExitOffer>;
const isSubscription = (p: string) => ["monthly", "annual"].includes(planOfProduct(p) ?? "");
export const PromoOffer = z.strictObject({
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, "expected a short id such as winback_50"),
  productId: ProductId.refine(isSubscription, "promotional offers are for subscriptions (monthly or annual)"),
  offerId: z.string().regex(/^[A-Za-z0-9._-]{1,64}$/, "expected the App Store Connect offer id"),
  audience: z.enum(PROMO_AUDIENCES),
  content: OfferCopy,
});
export type PromoOffer = z.infer<typeof PromoOffer>;
export const PromoOffers = z
  .array(PromoOffer)
  .max(10)
  .refine((xs) => distinct(xs.map((x) => x.id)), "offer ids must be distinct")
  .refine((xs) => distinct(xs.map((x) => `${x.productId}|${x.offerId}`)), "each (productId, offerId) once");
export const AllowedProducts = z
  .array(ProductId)
  .min(3)
  .max(30)
  .refine(distinct, "product ids must be distinct")
  .refine((xs) => Object.values(BASE_PRODUCTS).every((p) => xs.includes(p)), "the three base products (iqdaily.pro.monthly, .annual, .lifetime) must stay allowed");
export const Prices = z.record(ProductId, z.number().min(0).max(10_000)).refine((r) => Object.keys(r).length <= 30, "at most 30 products");

export const DEFAULT_EXIT_OFFER: ExitOffer = {
  enabled: false,
  productId: ANNUAL_OFFER_PRODUCT,
  content: { en: { title: "A welcome offer, just once", body: "Get a full year of IQ Daily Pro at a special price. This offer won’t come back.", badge: "One-time offer" } },
  maxShows: 1,
};

export const PAYWALL_CONFIG = {
  "paywall.onOpen": { schema: OnOpen, default: { firstLaunch: true, returning: "daily", everyDays: 3, minHoursBetween: 20 } as OnOpen },
  "paywall.defaultVariant": { schema: Variant, default: {} as Variant },
  "paywall.allowedProducts": { schema: AllowedProducts, default: [BASE_PRODUCTS.monthly, BASE_PRODUCTS.annual, BASE_PRODUCTS.lifetime, ANNUAL_OFFER_PRODUCT] as string[] },
  "paywall.exitOffer": { schema: ExitOffer, default: DEFAULT_EXIT_OFFER },
  "paywall.promoOffers": { schema: PromoOffers, default: [] as PromoOffer[] },
  "paywall.prices": {
    schema: Prices,
    default: { [BASE_PRODUCTS.monthly]: 5.99, [BASE_PRODUCTS.annual]: 29.99, [BASE_PRODUCTS.lifetime]: 99.99, [ANNUAL_OFFER_PRODUCT]: 19.99 } as Record<string, number>,
  },
} as const;

export function readPaywallConfig(values: Record<string, unknown> | undefined) {
  const get = <K extends keyof typeof PAYWALL_CONFIG>(k: K) => {
    const parsed = PAYWALL_CONFIG[k].schema.safeParse(values?.[k]);
    return (parsed.success ? parsed.data : PAYWALL_CONFIG[k].default) as z.infer<(typeof PAYWALL_CONFIG)[K]["schema"]>;
  };
  return {
    onOpen: get("paywall.onOpen"),
    defaultVariant: get("paywall.defaultVariant"),
    allowedProducts: get("paywall.allowedProducts"),
    exitOffer: get("paywall.exitOffer"),
    promoOffers: get("paywall.promoOffers"),
    prices: get("paywall.prices"),
  };
}

/** The keys the cross-key rule covers. */
export const PAYWALL_RELATED_KEYS: ReadonlySet<string> = new Set(["paywall.allowedProducts", "paywall.defaultVariant", "paywall.exitOffer", "paywall.promoOffers"]);

/**
 * The API's cross-key check (src/domain/paywall.ts paywallConfigProblem): every product the default variant, the
 * exit offer or a promotional offer uses must be in paywall.allowedProducts. Only relations touched by `changed`
 * are checked; `get` returns the value about to be stored, else the current one.
 */
export function paywallConfigProblem(changed: readonly string[], get: (key: string) => unknown): { key: string; message: string } | null {
  const touches = (...keys: string[]) => keys.some((k) => changed.includes(k));
  const allowed = get("paywall.allowedProducts") as string[];
  const blame = (key: string) => (changed.includes(key) ? key : "paywall.allowedProducts");
  const missing = (used: string[]) => [...new Set(used.filter((p) => !allowed.includes(p)))];
  if (touches("paywall.allowedProducts", "paywall.defaultVariant")) {
    const out = missing(variantProducts(get("paywall.defaultVariant") as Variant));
    if (out.length) return { key: blame("paywall.defaultVariant"), message: `products ${out.join(", ")} of paywall.defaultVariant are not in paywall.allowedProducts.` };
  }
  if (touches("paywall.allowedProducts", "paywall.exitOffer")) {
    const out = missing([(get("paywall.exitOffer") as ExitOffer).productId]);
    if (out.length) return { key: blame("paywall.exitOffer"), message: `productId ${out.join(", ")} of paywall.exitOffer is not in paywall.allowedProducts.` };
  }
  if (touches("paywall.allowedProducts", "paywall.promoOffers")) {
    const out = missing((get("paywall.promoOffers") as PromoOffer[]).map((o) => o.productId));
    if (out.length) return { key: blame("paywall.promoOffers"), message: `products ${out.join(", ")} of paywall.promoOffers are not in paywall.allowedProducts.` };
  }
  return null;
}

// ---------------------------------------------------------------- statistics

/** Wilson score interval (95%) for a proportion. */
export function wilson(successes: number, trials: number): { rate: number | null; low: number | null; high: number | null } {
  if (trials <= 0) return { rate: null, low: null, high: null };
  const z = 1.959963984540054;
  const p = successes / trials;
  const d = 1 + (z * z) / trials;
  const c = p + (z * z) / (2 * trials);
  const m = z * Math.sqrt((p * (1 - p)) / trials + (z * z) / (4 * trials * trials));
  return { rate: p, low: Math.max(0, c / d - m / d), high: Math.min(1, c / d + m / d) };
}

/** The product a plan uses in a variant: its override, else the base product. */
export const productFor = (v: Variant, plan: Plan) => v.products?.[plan] ?? BASE_PRODUCTS[plan];

/** "Save N%" for the yearly plan against twelve months, as the app computes it from the shown prices. */
export function savings(monthly: number | undefined, annual: number | undefined): number | null {
  if (!monthly || !annual) return null;
  const s = 1 - annual / (monthly * 12);
  return s > 0 ? Math.round(s * 100) : null;
}
